import { useEffect, useState, type FunctionComponent } from "react";
import { useGetCampaignsAdminHttp } from "../../../../hooks/http/admin/campaigns/get-campaigns.admin.http";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@radix-ui/react-tabs";
import {
	CampaignPlatform,
	CampaignStatus,
	type ICampaign,
} from "../../../../types/models/campaign.models.types";
import type { IPagination } from "../../../../types/models/pagination.models.types";
import DashboardCampaignsAdminPartial from "../dashboard-campaigns.admin.partials";
import HornIcon from "../../../../assets/svgs/campaigns/horn.svg";
import { useToast } from "../../../../hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { handleHttpError } from "../../../../utils/utils";

const tabs = [
	{
		title: "Active",
		status: CampaignStatus.live,
	},
	{
		title: "Admin draft",
		status: CampaignStatus.adminDraft,
	},
	{
		title: "Brand draft",
		status: CampaignStatus.brandDraft,
	},
	{
		title: "Closed",
		status: CampaignStatus.ended,
	},
];

interface CampaignMenuAdminPartialProps {
	searchData: ICampaign[];
	searching: boolean;
	search: string;
	setSearch: React.Dispatch<React.SetStateAction<string>>;
	status: CampaignStatus;
	setStatus: React.Dispatch<React.SetStateAction<CampaignStatus>>;
}

const CampaignMenuAdminPartial: FunctionComponent<
	CampaignMenuAdminPartialProps
> = ({ searchData, searching, search, status, setStatus, setSearch }) => {
	const {
		fetching: fetchingCampaigns,
		execute: getCampaignsHttp,
		cancel: cancelGetCampaignHttp,
	} = useGetCampaignsAdminHttp();

	const [campaignData, setCampaignData] = useState<ICampaign[]>([]);
	const [pageNo, setPageNo] = useState(1);
	const [paginationData, setPaginationData] = useState<IPagination>();
	const [statusParams, setStatusParams] = useSearchParams();

	const { toast } = useToast();

	async function getCampaigns(page: number, status: CampaignStatus) {
		cancelGetCampaignHttp();

		const response = await getCampaignsHttp({
			body: null,
			queryParams: { platform: CampaignPlatform.SOLO, page, status },
		});

		if (!response.success) {
			return handleHttpError(response, toast);
		}

		setCampaignData((prevData) => {
			const newCampaigns = response.data.campaigns.filter((campaign) => {
				return prevData.find((c) => c.id === campaign.id) !== null;
			});

			return [...prevData, ...newCampaigns];
		});

		setPaginationData(response.data.pagination);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getCampaigns(pageNo, status);

		if (statusParams.get("status")) {
			const status = statusParams.get("status") as CampaignStatus;
			setStatus(status);
		}
		if (statusParams.get("status") === null) {
			setStatusParams({ status: CampaignStatus.live });
		}
	}, [pageNo]);

	return (
		<div className="relative flex flex-col flex-1">
			<div className="flex justify-between items-center">
				<div className="flex items-center gap-2">
					<HornIcon />
					<span className="font-semibold text-2xl">Your campaigns</span>
				</div>
			</div>
			<div className="py-8">
				<Tabs
					value={statusParams.get("status") || status}
					onValueChange={(value) => {
						const nextpage = 1;
						const nextSatus = value as CampaignStatus;
						setPageNo(nextpage);
						setCampaignData([]);
						setSearch("");
						setStatus(nextSatus);
						getCampaigns(nextpage, nextSatus);
						setStatusParams({ status: nextSatus });
					}}
				>
					<TabsList>
						{tabs.map((tab) => {
							return (
								<TabsTrigger
									key={tab.status}
									className={`border-b-2 px-4 font-semibold py-2 ${tab.status === status ? "" : "border-border"}`}
									value={tab.status}
								>
									{tab.title}
								</TabsTrigger>
							);
						})}
					</TabsList>
					{tabs.map((tab) => {
						if (search.trim().length > 0) {
							return (
								<TabsContent
									key={tab.status}
									value={tab.status}
									className="py-12"
								>
									<DashboardCampaignsAdminPartial
										fetching={searching}
										campaignData={searchData}
										setPageNo={setPageNo}
										paginationData={paginationData as IPagination}
										search={search}
									/>
								</TabsContent>
							);
						}
						return (
							<TabsContent
								key={tab.status}
								value={tab.status}
								className="py-12"
							>
								<DashboardCampaignsAdminPartial
									fetching={fetchingCampaigns}
									campaignData={campaignData}
									setPageNo={setPageNo}
									paginationData={paginationData as IPagination}
								/>
							</TabsContent>
						);
					})}
				</Tabs>
			</div>
			{/* biome-ignore lint/a11y/useButtonType: <explanation> */}
			<button
				id="getCampaigns"
				onClick={() => {
					setCampaignData([]);
					getCampaigns(1, status);
				}}
				className="hidden"
			/>
		</div>
	);
};

export default CampaignMenuAdminPartial;
